
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader } from "lucide-react";
import Navbar from "@/components/Navbar";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ProductFormBasicInfo from "@/components/product/ProductFormBasicInfo";
import ProductFormInventory from "@/components/product/ProductFormInventory";
import ProductFormGroups from "@/components/product/ProductFormGroups";
import ProductFormActions from "@/components/product/ProductFormActions";
import ProductFormSupplier from "@/components/product/ProductFormSupplier";
import { useEditProductForm } from "@/hooks/use-edit-product-form";

interface EditProductPageProps {
  productId?: string;
}

const EditProductPage = ({ productId }: EditProductPageProps) => {
  const params = useParams<{ productId: string }>();
  // Use the passed productId or get it from the URL params as fallback
  const finalProductId = productId || params.productId;
  const navigate = useNavigate();
  
  console.log(`EditProductPage rendering with productId: ${finalProductId}`);
  
  const {
    product,
    formState,
    setters,
    handleGroupToggle,
    handleSubmit,
    groups,
    isSubmitting,
    isSubmitDisabled,
    isLoading
  } = useEditProductForm(finalProductId);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <main className="page-container">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate("/products")}
              className="mr-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold">Edit Product</h1>
          </div>
          <div className="flex justify-center items-center py-20">
            <Loader className="h-8 w-8 animate-spin text-primary" />
            <p className="ml-4 text-lg">Loading product data...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <main className="page-container">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate("/products")}
              className="mr-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold">Product Not Found</h1>
          </div>
          <div className="flex flex-col justify-center items-center py-20">
            <p className="text-lg mb-4">The product you're looking for couldn't be found.</p>
            <Button onClick={() => navigate("/products")}>Return to Products</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="page-container max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/products")}
            className="mr-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Edit Product</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Product Details</CardTitle>
              <CardDescription>
                Update information about the product
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ProductFormBasicInfo
                name={formState.name}
                setName={setters.setName}
                description={formState.description}
                setDescription={setters.setDescription}
                category={formState.category}
                setCategory={setters.setCategory}
              />
              
              <ProductFormInventory
                currentStock={formState.currentStock}
                setCurrentStock={setters.setCurrentStock}
                usageRate={formState.usageRate}
                setUsageRate={setters.setUsageRate}
                minStockAlert={formState.minStockAlert}
                setMinStockAlert={setters.setMinStockAlert}
                packageSize={formState.packageSize}
                setPackageSize={setters.setPackageSize}
              />
              
              <ProductFormSupplier
                supplierName={formState.supplierName}
                setSupplierName={setters.setSupplierName}
                supplierWebsite={formState.supplierWebsite}
                setSupplierWebsite={setters.setSupplierWebsite}
                supplierContact={formState.supplierContact}
                setSupplierContact={setters.setSupplierContact}
                productCode={formState.productCode}
                setProductCode={setters.setProductCode}
              />
              
              <ProductFormGroups
                groups={groups}
                selectedGroups={formState.selectedGroups}
                handleGroupToggle={handleGroupToggle}
              />
            </CardContent>
            <CardFooter>
              <ProductFormActions
                onCancel={() => navigate("/products")}
                isSubmitting={isSubmitting}
                isSubmitDisabled={isSubmitDisabled}
              />
            </CardFooter>
          </Card>
        </form>
      </main>
    </div>
  );
};

export default EditProductPage;
